// Рейтинги: таблицы номинаций с сервера (GET api/leaderboard). Грузятся при входе на экран рейтингов —
// каждый раз заново (свежая партия могла только что уйти на сервер); пока идёт загрузка, видна прошлая таблица.
import type { Leaderboards, RatingChange } from '../types.ts';
import { clientId, flushOutbox } from './outbox.ts';
import { st } from './store.ts';
import { gameAchItems, celebrate } from './ach.ts';
import { achItem, hasItem, pushItem } from './inbox.ts';

function isLeaderboards(x: unknown): x is Leaderboards {
  if (!x || typeof x !== 'object') return false;
  const o = x as { people?: { rows?: unknown }; bots?: unknown };
  return Array.isArray(o.people?.rows) && !!o.bots && typeof o.bots === 'object';
}

function isRatingChange(x: unknown): x is RatingChange {
  if (!x || typeof x !== 'object') return false;
  const o = x as Partial<RatingChange>;
  return (o.cat === 'bots' || o.cat === 'people') && typeof o.rankAfter === 'number' && typeof o.rankBefore === 'number';
}

/**
 * Сдвиг в рейтинге за законченную партию с сидом seed — один запрос на партию. Хотсит-запись сначала уходит на
 * сервер (очередь), онлайн-партию сервер зачёл сам до того, как разослал её конец. Нет сети или партия не
 * в рейтинге — плашки просто нет.
 */
export async function loadRatingChange(seed: number, repaint: () => void): Promise<void> {
  if (st.rating.seed === seed) return;
  st.rating = { seed, data: null, loading: true };
  try {
    await flushOutbox();
    const r = await fetch(`api/rating-change?client=${encodeURIComponent(clientId())}&seed=${seed}`);
    const j: unknown = r.ok ? await r.json() : null;
    if (st.rating.seed !== seed) return;
    st.rating.data = isRatingChange(j) ? j : null;
    if (st.rating.data) announceAchievements(st.rating.data, seed);
  } catch {
    /* нет сети — без плашки */
  } finally {
    if (st.rating.seed === seed) {
      st.rating.loading = false;
      repaint();
    }
  }
}

/** Новые ачивки партии (впервые у игрока): всплывающими карточками и в ящик — один раз на партию (ящик помнит её seed) */
function announceAchievements(rc: RatingChange, seed: number): void {
  if (hasItem(`ach:${seed}`)) return;
  const items = gameAchItems(rc);
  if (!celebrate(items)) return;
  pushItem(achItem(seed, items.filter((a) => a.first).map((a) => a.name)));
}

/** Загрузить таблицы; repaint — перерисовать экран, когда ответ пришёл (или не пришёл) */
export async function loadLeaders(repaint: () => void): Promise<void> {
  if (st.leaders.loading) return;
  st.leaders.loading = true;
  repaint();
  try {
    // сначала — неотправленные партии этого устройства: только что сыгранная должна попасть в таблицу
    await flushOutbox();
    const r = await fetch(`api/leaderboard?client=${encodeURIComponent(clientId())}`);
    const j: unknown = r.ok ? await r.json() : null;
    if (!isLeaderboards(j)) throw new Error('bad leaderboard');
    st.leaders.data = j;
    st.leaders.error = false;
  } catch {
    st.leaders.error = true;
  } finally {
    st.leaders.loading = false;
    repaint();
  }
}
